import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapPin, Storefront } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { TID } from "@/lib/testIds";

export default function DriverDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [available, setAvailable] = useState(user?.driver_available || false);
  const [avail, setAvail] = useState([]);
  const [mine, setMine] = useState([]);

  const load = async () => {
    const [a, m] = await Promise.all([
      api.get("/driver/available-orders"),
      api.get("/driver/my-orders"),
    ]);
    setAvail(a.data);
    setMine(m.data);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const toggle = async (val) => {
    setAvailable(val);
    await api.post("/driver/availability", { available: val });
    await refresh();
  };

  const accept = async (orderId) => {
    try {
      await api.post(`/driver/orders/${orderId}/accept`);
      toast.success("Order accepted");
      navigate(`/driver/orders/${orderId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    }
  };

  const activeMine = mine.filter((m) =>
    ["assigned", "picked_up", "delivering"].includes(m.order.status)
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <span className="label-eyebrow">Dispatch</span>
          <h1 className="mt-1 font-display text-5xl">Ready to roll?</h1>
        </div>
        <div className="card-panel p-4 flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full border-2 border-[var(--text)] ${available ? "bg-[var(--green)]" : "bg-[var(--text-3)]"}`} />
          <span className="font-bold">{available ? "Online" : "Offline"}</span>
          <Switch data-testid={TID.driverAvailToggle} checked={available} onCheckedChange={toggle} />
        </div>
      </div>

      {activeMine.length > 0 && (
        <section>
          <h2 className="font-display text-2xl mb-3">Active delivery</h2>
          <div className="space-y-3">
            {activeMine.map(({ order, store }) => (
              <button
                key={order.order_id}
                onClick={() => navigate(`/driver/orders/${order.order_id}`)}
                className="w-full card-panel p-5 text-left"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-xl">
                      #{order.order_id.slice(-6).toUpperCase()} · {store.name}
                    </p>
                    <p className="text-sm text-[var(--text-2)] mt-1 inline-flex items-center gap-1">
                      <MapPin size={12} weight="fill" /> {order.delivery_address}
                    </p>
                  </div>
                  <span className="chip" style={{ background: "var(--yellow)" }}>
                    {order.status.replace(/_/g, " ")}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display text-2xl mb-3">Available orders ({avail.length})</h2>
        {avail.length === 0 ? (
          <div className="card-panel p-8 text-center text-[var(--text-2)]">
            No deliveries waiting. Stay online — new orders drop in real time.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {avail.map(({ order, store }) => (
              <div key={order.order_id} className="card-panel p-5">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--text-2)]">
                  <Storefront size={12} weight="fill" /> {store.name}
                </div>
                <p className="mt-3 font-display text-xl">
                  {order.items.reduce((n, it) => n + it.quantity, 0)} items · R{order.total.toFixed(2)}
                </p>
                <p className="mt-1 text-sm text-[var(--text-2)] inline-flex items-center gap-1">
                  <MapPin size={12} weight="fill" /> {order.delivery_address}
                </p>
                <p className="mt-3 text-sm font-black text-[var(--green)]">
                  Earn ~R{(order.delivery_fee * 0.8).toFixed(2)}
                </p>
                <button data-testid={TID.driverAcceptBtn} onClick={() => accept(order.order_id)} className="btn-primary w-full mt-4">
                  Accept
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
